import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import './header.css';

const Header = () => {
  const [activeNav, setActiveNav] = useState('#home');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [Toggle, showMenu] = useState(false);

  // Variants untuk animasi matahari/bulan
  const raysVariants = {
    hidden: {
      strokeOpacity: 0,
      transition: {
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
    visible: {
      strokeOpacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const rayVariant = {
    hidden: {
      pathLength: 0,
      opacity: 0,
      scale: 0
    },
    visible: {
      pathLength: 1,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        pathLength: { duration: 0.3 },
        opacity: { duration: 0.2 },
        scale: { duration: 0.3 }
      }
    },
  };

  const shineVariant = {
    hidden: {
      opacity: 0,
      scale: 2,
      strokeDasharray: "20, 1000",
      strokeDashoffset: 0,
      filter: "blur(0px)",
    },
    visible: {
      opacity: [0, 1, 0],
      strokeDashoffset: [0, -50, -100],
      filter: ["blur(2px)", "blur(2px)", "blur(0px)"],
      transition: {
        duration: 0.75,
        ease: 'linear'
      },
    },
  };

  const sunPath = "M70 49.5C70 60.8218 60.8218 70 49.5 70C38.1782 70 29 60.8218 29 49.5C29 38.1782 38.1782 29 49.5 29C60 29 69.5 38 70 49.5Z";
  const moonPath = "M70 49.5C70 60.8218 60.8218 70 49.5 70C38.1782 70 29 60.8218 29 49.5C29 38.1782 38.1782 29 49.5 29C39 45 49.5 59.5 70 49.5Z";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY >= 80);

      const sections = ['home', 'about', 'skills', 'services', 'portfolio', 'contact'];
      let currentSection = '';

      sections.forEach((section) => {
        const sectionElement = document.getElementById(section);
        if (sectionElement) {
          const rect = sectionElement.getBoundingClientRect();
          const isVisible = rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2;

          if (section === 'services' || section === 'qualification' || section === 'testimonial') {
            const servicesElement = document.getElementById('services');
            const qualificationElement = document.getElementById('qualification');
            const testimonialElement = document.getElementById('testimonial');

            if (
              (servicesElement && servicesElement.getBoundingClientRect().top <= window.innerHeight / 2) ||
              (qualificationElement && qualificationElement.getBoundingClientRect().top <= window.innerHeight / 2) ||
              (testimonialElement && testimonialElement.getBoundingClientRect().top <= window.innerHeight / 2)
            ) {
              currentSection = '#services';
            }
          } else if (isVisible) {
            currentSection = `#${section}`;
          }
        }
      });

      setActiveNav(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark');
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.body.className = isDarkMode ? 'dark-mode' : 'light-mode';
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode((prevMode) => !prevMode);
  };

  const menuItems = [
    { name: 'home', icon: 'home' },
    { name: 'about', icon: 'user' },
    { name: 'skills', icon: 'file-alt' },
    { name: 'services', icon: 'briefcase-alt' },
    { name: 'portfolio', icon: 'scenery' },
    { name: 'contact', icon: 'message' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: -20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    }
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <nav className="nav container">
        <motion.a 
          href="#home" 
          className={`nav__logo ${activeNav === '#home' ? 'active-link' : ''}`}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          Alvin <span className="nav__logo-dot"> .</span>
        </motion.a>

        <motion.div 
          className={`nav__menu ${Toggle ? 'show-menu' : ''}`}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.ul 
            className="nav__list"
            variants={containerVariants}
          >
            {menuItems.map((item, index) => (
              <motion.li 
                className="nav__item" 
                key={item.name}
                variants={itemVariants}
                custom={index}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <a
                  href={`#${item.name}`}
                  onClick={() => setActiveNav(`#${item.name}`)}
                  className={`nav__link ${activeNav === `#${item.name}` ? 'active-link' : ''}`}
                >
                  <i className={`uil uil-${item.icon} nav__icon`}></i>
                  {item.name.charAt(0).toUpperCase() + item.name.slice(1)}
                </a>
              </motion.li>
            ))}
          </motion.ul>
          <motion.i 
            className="uil uil-times nav__close"
            onClick={() => showMenu(false)}
            whileHover={{ scale: 1.1, rotate: 90 }}
          />
        </motion.div>

        <motion.button 
  className={`change-theme ${isDarkMode ? 'text-white' : 'text-black'}`} 
  onClick={toggleTheme}
  whileHover={{ scale: 1.1 }}
  whileTap={{ scale: 0.9 }}
  initial={{ opacity: 0 }}
  animate={{ opacity: 1 }}
  transition={{ delay: 0.8 }}
>
  <motion.svg
    strokeWidth="4"
    strokeLinecap="round"
    width={30}
    height={30}
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className='relative'
  >
    <motion.path 
      variants={shineVariant} 
      d={moonPath} 
      className='absolute top-0 left-0'
      stroke="currentColor"
      initial="hidden" 
      animate={isDarkMode ? 'visible' : "hidden"} 
    />

    <motion.g 
      variants={raysVariants} 
      initial='hidden' 
      animate={!isDarkMode ? "visible" : "hidden"} 
      style={{ strokeLinecap: 'round' }}
      stroke="currentColor"
    >
      {[...Array(8)].map((_, i) => (
        <motion.path 
          key={i} 
          variants={rayVariant} 
          d={getRayPath(i)} 
          stroke="currentColor"
        />
      ))}
    </motion.g>

    <motion.path
      d={sunPath}
      fill="currentColor"
      transition={{ duration: 1, type: "spring" }}
      initial={{ opacity: 0 }}
      animate={
        isDarkMode
          ? {
              d: moonPath,
              rotate: -360,
              scale: 1.2,
              opacity: 1,
              fill: "currentColor",
            }
          : {
              d: sunPath,
              rotate: 0,
              opacity: 1,
              fill: "currentColor",
            }
      }
    />
  </motion.svg>
</motion.button>

        <motion.div 
          className="nav__toggle"
          onClick={() => showMenu(!Toggle)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <i className="uil uil-apps" />
        </motion.div>
      </nav>
    </header>
  );
};

// Fungsi untuk mendapatkan path sinar matahari
const getRayPath = (index) => {
  const angles = [
    [50, 2, 50, 11],
    [85, 15, 78, 22],
    [98, 50, 89, 50],
    [85, 85, 78, 78],
    [50, 98, 50, 89],
    [23, 78, 16, 84],
    [11, 50, 2, 50],
    [23, 23, 16, 16]
  ];
  return `M${angles[index][0]} ${angles[index][1]}L${angles[index][2]} ${angles[index][3]}`;
};

export default Header;